import {Evented} from "@/engine/evented.js";
import {mapContext, shortLivedTokenManager} from "@/context/global.js";
import * as L from "leaflet"
import {AMUtil} from "@/engine/leaflet/antimeridianUtils.js";
import {elog} from "@/engine/helpers/logging.js";

// Query parameter accepted by the auth gateway for native browser resource requests.
export const RESOURCE_AUTH_TOKEN_PARAMETER = "mtoken";

export const LAYER_TYPE_VECTOR_LINE = "line"
export const LAYER_TYPE_VECTOR_POINT = "point"
export const LAYER_TYPE_VECTOR_POLYGON = "polygon"
export const LAYER_TYPE_VECTOR_ELEVATION = "elevation"
export const LAYER_TYPE_VECTOR_COMMON = "common"

export class Layer extends Evented {
    constructor(struct) {
        super()
        this.id                 = struct.id;
        this.name               = struct.name;
        this.workspace          = struct.workspace;
        this.visible            = typeof struct.visible !== 'undefined' ? struct.visible : true;
        this.writable           = struct.writable;
        this.createdAt          = struct.createdAt; // todo use Date/Time JS Class to store date correctly
        this.bounds             = null; // latlng bounds
        this.dataset_bounds     = null; // is set from struct.bounds
                                        // usually equals this.bounds
                                        // is used to get direct WMS or tile requests
        this.srs                = struct.srs;
        this.style              = {};
        // styleKeys contains the map of style key
        // and a correspond UI style update method.
        // the layer can contain different style data for different
        // views or components
        this.styleKeys          = {};
        this.styleOptions       = struct.styleOptions ? struct.styleOptions : []
        this.defaultStyle       = struct.defaultStyle ? struct.defaultStyle : {}
        // the key of style that was processed
        // by setStyle and getStyle if style key option isn't set
        this.defaultStyleKey    = struct.defaultStyleKey ? struct.defaultStyleKey : ""
        this.debug              = (struct && struct.debug) ? struct.debug : false;
        this.preview            = struct.preview;
        this.order              = typeof struct.order !== 'undefined' ? struct.order : 0;
        this.map                = null;
        this.layer              = null;

        this.server             = struct.server

        this.updatePropertiesHandler = null;
        this.defaultName        = "layer"

        //logging function
        this.elog = (typeof elog === "function") ? elog : function (severity, message) {
            if (typeof console[severity.toLowerCase()] === "function") {
                console[severity.toLowerCase()](message);
            }
        };

        this._initStyle(struct.style, struct.migrationStyleTo)
        this._initBounds(struct.bounds);
        this.updatePropertiesHandler = mapContext.RPCUpdateProperties;
    }

    // init layer style from the backend data
    _initStyle(style, migrateToStyleKey) {
        if (! style)
            return

        let s = {}
        if (typeof style === "object") {
            s = style
        } else {
            try {
                s = JSON.parse(style)
            } catch (e) {
                this.elog("backend returns wrong style data")
            }
        }
        this.style = s

        // migration from old style format
        if (migrateToStyleKey) {
            // check for old format
            let oldFormat = false
            for (let o of this.styleOptions ) {
                if (o in this.style) {
                    oldFormat = true
                    break
                }
            }
            if (oldFormat) {
                let l = this.style
                this.style = {}
                this.style[migrateToStyleKey] = l
            }
        }

        // apply default style options
        for (let key in this.defaultStyle) {
            this.setStyle(this.defaultStyle[key], key,true)
        }
    }

    // init bounds from backend data
    _initBounds(bounds) {
        if (bounds && (bounds.min.lat || bounds.min.lng || bounds.max.lat || bounds.max.lng)) {
            this.dataset_bounds = L.latLngBounds([bounds.min.lat, bounds.min.lng], [bounds.max.lat, bounds.max.lng]);
            if (bounds.min.lat > 90 || bounds.min.lat < -90 ||
                bounds.max.lat > 90 || bounds.max.lat < -90) {
                // wrong bounds coords
                return;
            }
            this.bounds = L.latLngBounds([bounds.min.lat, bounds.min.lng], [bounds.max.lat, bounds.max.lng])
//            if (typeof AMUtil !== 'undefined')
  //              AMUtil.fixLng(this.bounds);
        }
        // extends workspace bounds to cover all layers
    }

    addOnMap(map) {
        // should be overridden and called at the beginning
        if (this.workspace == null || this.workspace.id == null) {
            this.elog("ERROR", `Can't add layer ${this.id} to the map, workspaceId isn't defined`)
            return
        }
        this.map = map;
    }

    removeFromMap() {
        if (! this.map)
            return
        // should be called at the end of cleanup if overridden
        if (this.map.hasLayer(this.layer)) {
            this.map.removeLayer(this.layer)
            this.layer = null
            this.map = null
        }
    }

    show(byUser = false) {
        this.visible = true
        /*
        if (this.hiddenByUser && ! byUser) {
            this._logdebug("showing is suppressed as was hidden by user")
            return
        } else {
            this.hiddenByUser = false
        }
        // crunch, need to refactored
        if (this.workspace.hiddenByZoomlevel) {
            this._logdebug("showing is suppressed as workspace is hidden by zoom level")
            return
        }
         */
        this._show()
        this.fire("visiblechanged")
        mapContext.fire("layer:visiblechanged", {layer: this});
    }

    hide(byUser = false) {
        this.visible = false
        //this.hiddenByUser = byUser
        this._hide()
        this.fire("visiblechanged")
        mapContext.fire("layer:visiblechanged", {layer: this});
    }

    // should be called internal
    _show() {
        if (! this.visible) {
            this._logdebug("showing is suppressed (visible = false)")
            return
        }

        if (!this.workspace.isVisibleReal()) {
            this._logdebug("showing is suppressed as workspace is hidden")
            return
        }

        if (this.layer) {
            this.layer.addTo(this.map)
            this._logdebug("has been shown")
        }
    }

    // should be called internal
    _hide() {
        if (this.layer) {
            this.layer.remove()
            this._logdebug("has been hidden")
        }
    }

    _fireNew() {
        mapContext.fire("layer:new", this);
    }

    // get bounds with AM fixing
    getBounds() {
        if (! this.bounds)
            return null;
        let lb = L.latLngBounds(this.bounds.getSouthWest(), this.bounds.getNorthEast())
        //console.log(lb)
        if (typeof AMUtil !== 'undefined')
            AMUtil.fixLng(lb);
        return lb;
    }

    getPreview() {
        if (this.preview?.startsWith("/preview/")) {
            return this.preview
        }
        return this.addAuthToken(this.preview, shortLivedTokenManager.getToken())
    }

    /**
     * Adds, replaces, or removes the short-lived authentication token in a resource URL.
     * @param {string|null|undefined} url Candidate resource URL.
     * @param {string|null} token Raw short-lived token, or null to remove the parameter.
     * @returns {string|null|undefined} Updated URL in the same relative or absolute form as the input.
     */
    addAuthToken(url, token) {
        if (!url) {
            return url;
        }

        let parsedUrl;
        try {
            parsedUrl = new URL(url, window.location.origin);
        } catch {
            return url;
        }

        if (token == null) {
            parsedUrl.searchParams.delete(RESOURCE_AUTH_TOKEN_PARAMETER);
        } else {
            parsedUrl.searchParams.set(RESOURCE_AUTH_TOKEN_PARAMETER, token);
        }

        if (/^[a-z][a-z\d+.-]*:/i.test(url)) {
            return parsedUrl.toString();
        }
        return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }

    /**
     * Joins this layer's server URL with a resource path while avoiding duplicate slashes.
     * @param {string} resourcePath Path relative to the layer server base.
     * @returns {string} Absolute URL or same-origin absolute path matching the server format.
     */
    _joinServerUrl(resourcePath) {
        return `${this.server.replace(/\/+$/, "")}/${resourcePath.replace(/^\/+/, "")}`
    }

    isVisible() {
        return this.visible && this.workspace.isVisible();
    }

    isWrongSrs() {
        return (this.srs !== 'undefined' && (this.srs === "" || this.srs === "EPSG:404000"));
    }

    isVisibleReal() {
        return this.visible && this.workspace.isVisibleReal();
    }

    updateProperties(struct, fromSuper = false) {
        let changed = false;
        for (let prop of ["name", "srs", "elevation"]) {
            if (struct[prop] !== undefined) {
                if (prop === "name") {
                    if (struct[prop] === "")
                        struct[prop] = this.defaultName;
                }
                if (prop === "srs" && struct[prop]) {
                    let n = struct[prop].match(/\d+/);
                    if (n) {
                        struct[prop] = 'EPSG:' + n[0];
                    } else {
                        continue;
                    }
                }
                if (this[prop] !== struct[prop]) {
                    changed = true;
                    this[prop] = struct[prop];
                }
            }
        }

        // calls the update method only if this method is called directly
        // not from super from a child class
        if ( ! fromSuper ) {
            if (changed) {
                if (this.updatePropertiesHandler)
                    this.updatePropertiesHandler(this);
                mapContext.fire("layer:updateProperties", {layer: this, properties: struct});
            }
        }

        return changed;
    }

    // applies new style to an existent layers
    setStyle(style, styleKey, dntOverwrite) {
        if (! style || typeof style !== 'object') {
            return
        }

        if (!styleKey)
            styleKey = this.defaultStyleKey

        if (! (styleKey in this.style)) {
            this.style[styleKey] = {}
        }
        // saves style to the struct
        for (let o of this.styleOptions) {
            if (o in style) {
                if (dntOverwrite) {
                    if (! (o in this.style[styleKey]))
                        this.style[styleKey][o] = style[o]
                } else {
                    this.style[styleKey][o] = style[o]
                }
            }

        }
        // apply style the style
        if (this.styleKeys[styleKey] &&
            typeof this.styleKeys[styleKey] === "function") {
            this.styleKeys[styleKey](this.style[styleKey])
        }
    }

    getStyle(styleKey) {
        if (!styleKey)
            styleKey = this.defaultStyleKey
        if (! this.style ||
            typeof this.style !== 'object' ||
            ! (styleKey in this.style)
        ) {
            return {}
        }
        return this.style[styleKey]
    }

    _logdebug(msg) {
        if (this.debug)
            this.elog("DEBUG", `Layer ${this.name}:${this.id} ${msg}`)
    }
}
